<template>
    <div>
        <v-dialog v-model="itemdialog" max-width="900px">
            <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-4" v-on="on">{{$t('pmformsetup.newitem')}}</v-btn>
            </template>
            <v-card>
            <v-card-title class="subtitle-1 mb-4">
                {{ formData.secttype }} - {{ isEdit ? $t('Setupformreviewsectiondetails.edititem') : $t('Setupformreviewsectiondetails.addnewitem') }}
            </v-card-title>
            <v-card-text>
                <v-container>
                    <!-- first row -->
                    <v-row>
                        <v-col cols="10" class="pb-n4 mt-n8">
                            <v-text-field
                            outlined
                            :label="$t('Setupratingset.label')"
                            type="text"
                            :rules="[rules.required]"
                            v-model="formData.label"
                            dense
                            class="v-input"
                            ></v-text-field>
                        </v-col>

                        <v-col v-if="formData.secttype === 'Scorable Section'" cols="2" class="pb-n4 mt-n8">
                            <v-text-field
                            outlined
                            :label="$t('Setupformreviewsectiondetails.order')"
                            type="number"
                            :rules="[rules.required, rules.integer]"
                            v-model="formData.orderno"
                            dense
                            class="v-input"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <!-- end of first row -->
                    <!-- cover whole column and row -->
                    <v-row>
                        <v-col cols="12" class="pb-n4 mt-n8"> 
                            <v-textarea
                            v-model="formData.description"
                            outlined
                            :label="$t('columns.description')"
                            type="textarea" 
                            :rules="[rules.required]"
                            dense
                            rows="10" 
                            class="v-input"
                            style="resize: vertical;" 
                            ></v-textarea>
                        </v-col>
                    </v-row>
                    <!-- end of whole column and row -->
                    <!-- third row -->
                    <v-row v-if="formData.secttype === 'Scorable Section'">
                        <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                            <v-text-field
                            outlined
                            :label="$t('Setupformreviewsection.weightage')"
                            type="number"
                            :rules="[rules.positivemax100, rules.required]"
                            v-model="formData.weightage"
                            dense
                            class="v-input"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="2" sm="2" class="pb-n4 mt-n8">
                            <v-checkbox
                            outlined
                            ref="changeWeightage"
                            v-model="formData.changeWeightage"
                            :label="$t('Setupformreviewsectiondetails.changeweightage')"
                            dense
                            ></v-checkbox>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col v-if="formData.secttype === 'Comments'" cols="2" sm="2" class="pb-n4 mt-n8">
                            <v-checkbox
                            outlined
                            ref="isCompulsory"
                            v-model="formData.isCompulsory"
                            :label="$t('Setupformreviewsectiondetails.compulsory')"
                            dense
                            ></v-checkbox>
                        </v-col>
                    </v-row>
                    <!-- end of third row -->
                    <!-- last row -->
                    <v-row>
                        <v-col v-if="formData.secttype === 'Scorable Section'" cols="6" sm="6" class="pb-n4 mt-n8">
                            <v-autocomplete
                                outlined
                                :label="$t('Setupformreviewsectiondetails.displayoption')"
                                :items="displayOptionList"
                                item-text="text"
                                item-value="value"
                                :rules="[rules.required]"
                                v-model="formData.pmdisplayoptionid"
                                dense
                                class="v-input"
                            ></v-autocomplete>
                        </v-col>
                    </v-row>
                    <!-- end of last row -->
                </v-container>
            </v-card-text>

            <v-card-text v-if="isErr" class="mt-n12">
                <span style="font-weight: bold; color: red">{{errMsg}}</span>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="green darken-1" text @click="saveItemDialog">{{$t('button.save')}}</v-btn>
              <v-btn color="red darken-1" text @click="closeItemDialog">{{$t('button.cancel')}}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-row>
            <v-cols cols="12" sm="10">
                <v-data-table v-if="!loading"
                    :headers="tabHeader"
                    :items="detailItem"
                    class="elavation-1"
                    fixed-header
                    :sort-by="['code']"
                >
                    <template v-slot:[`item.action`]="{ item }">
                        <v-icon
                            small
                            class="mr-2"
                            @click="modifyItemHandler(item)"
                        >
                            mdi-pen
                        </v-icon>
                        <v-icon
                            small
                            class="ml-3 mr-2"
                            @click="deleteItemHandler(item)"
                        >
                            mdi-delete
                        </v-icon>
                    </template>

                    <div v-if="isErr">
                    <app-error
                        v-bind:parentError="{
                        data: this.errMsg,
                        isErr: this.isErr,
                        }"
                        v-on:closeError="isErr = $event"
                    />
                    </div>

                </v-data-table>
            </v-cols>
        </v-row>
    </div>
</template>

<script>
import ErrorDialog from "../../../components/common/ErrorDialog";

export default {
    props: {
        parentObject: Object
    },

    components: {
        "app-error": ErrorDialog,
    },

    data() {
        return {
            rules: {
                required: value => !!value || this.$t('reportsparam.required'),
                integer: value =>
                    (Number.isInteger(Number(value)) && value >= 0) || this.$t('reportsparam.nonnegativeInteger'),
                positiveintmax100: value => {
                    const num = Number(value);
                    return (Number.isInteger(num) && num >= 0 && num <= 100)
                        ? true
                        : this.$t('reportsparam.integer0to100');
                },
                positivemax100: value => {
                    const num = Number(value);
                    return (num >= 0 && num <= 100)
                        ? true
                        : this.$t('reportsparam.number0to100');
                },
                positiveNumber: value => {
                    const num = Number(value);
                    return (num > 0) || this.$t('reportsparam.positiveNumber');
                }
            },

            itemdialog: false,
            //Loading for List of Form Sections
            loading: true,
            
            //Lists
            displayOption: [],
            displayOptionList: [],
            detailItem: [],

            formData: {
                pmreviewformsectionid: this.parentObject.sectid,
                secttype: this.parentObject.secttype,
                label: null,
                orderno: null,
                description: null,
                weightage: this.parentObject.sectweightage,
                changeWeightage: false,
                pmdisplayoptionid: null,
                isCompulsory: false,
                rectimestamp: null,
                updatedby: null,
                stamp: 0,
                userid: this.$store.getters.userId,
            },

            isEdit: false,

            //error msg and dialog, check for duplicates vars
            isErr: false,
            errMsg: '',
        }
    },

    computed: {
        allowDecimalRules() {
            return this.formData.allowDecimal 
            ? [this.rules.positivemax100] 
            : [this.rules.positiveintmax100];
        },

        tabHeader() {
            // Check for secttype and return different tab headers
            if (this.formData.secttype === 'Scorable Section') {
                return [
                    { text: this.$t('columns.actions'), value: 'action', width: '100px', sortable: false},
                    { text: this.$t('Setupformreviewsectiondetails.itemlabel'), value: 'label', width: '100px', sortable: false},
                    { text: this.$t('Setupformreviewsectiondetails.order'), value: 'orderno', width: '200px', sortable: true},
                    { text: this.$t('Setupformreviewsectiondetails.weightage'), value: 'weightage', width: '200px', sortable: true},
                    { text: this.$t('Setupformreviewsectiondetails.displayoption'), value: 'displayoptiontext', width: '200px', sortable: true},
                    { text: this.$t('Setupformreviewsectiondetails.changeweightage'), value: 'changeWeightagetext', width: '200px', sortable: true},
                ];
            } else if (this.formData.secttype === 'Comments') {
                return [
                    { text: this.$t('columns.actions'), value: 'action', width: '100px', sortable: false},
                    { text: this.$t('Setupformreviewsectiondetails.itemlabel'), value: 'label', width: '100px', sortable: false},
                    { text: this.$t('Setupformreviewsectiondetails.description'), value: 'description', width: '200px', sortable: true},
                    { text: this.$t('Setupformreviewsectiondetails.compulsory'), value: 'isCompulsorytext', width: '200px', sortable: true},
                ];
            }
            // Default tabHeader 
            return [
                { text: this.$t('columns.actions'), value: 'action', width: '100px', sortable: false},
                { text: this.$t('Setupformreviewsectiondetails.itemlabel'), value: 'label', width: '100px', sortable: false},
                { text: this.$t('Setupformreviewsectiondetails.description'), value: 'description', width: '200px', sortable: true},
            ];
        }
    },

    methods: {
        async loadReviewFormSectionItem() {
            const vm = this;

            try {
                const res = await this.$store.dispatch('get', { url: "/reviewformsection/item/" + this.formData.pmreviewformsectionid });

                vm.detailItem = res.data.rec.map(item => {
                    const displayOptionType = vm.displayOptionList.find(type => type.value === item.pmdisplayoptionid) || {};

                    return {
                        ...item,
                        changeWeightagetext: item.changeWeightage === 1 ? 'Yes' : 'No',
                        isCompulsorytext: item.isCompulsory === 1 ? 'Yes' : 'No',
                        displayoptiontext: displayOptionType.text || 'Unknown'
                    };
                });
            } catch (err) {
                vm.isErr = !vm.isErr;
                vm.errMsg = err;
            }
        },

        loadDisplayOption() {
            const vm = this;

            return this.$store.dispatch('get', { url: "/reviewform/displayoption/all" })
                .then(function (res) {
                    vm.displayOption = res.data.rec;

                    for (let i = 0; i < vm.displayOption.length; i++) {
                        vm.displayOptionList.push({
                            text: vm.displayOption[i].description,
                            value: vm.displayOption[i].id,
                        });
                    }
                })
                .catch(function (err) {
                    vm.errMsg = err;
                    console.error("Error loading display options:", err); 
                });
        },


        modifyItemHandler(item) {
            this.resetSelections();
            this.isEdit = true;
            this.formData = { ...this.formData, ...item };
            this.itemdialog = true;
        },

        deleteItemHandler(item) {
            const vm = this;

            if (!this.CheckPermission(this.$route.meta.funcname, "candelete")) {
                return false;
            }

            if (confirm(this.$t('pmformsetup.deleteitem', { code: item.label }))) {
                this.$store.dispatch('delete', {url: "/reviewformsection/item/" + item.id})
                .then(function () {
                    const index = vm.detailItem.findIndex(detail => detail.id === item.id);
                    if (index !== -1) {
                        vm.detailItem.splice(index, 1);
                    }
                })
                .catch(function (err) {
                    vm.errMsg = err;
                });
            }
        },

        saveItemDialog() {
            const vm = this;

            if (this.isEdit) {//Edit
                if (!this.CheckPermission(this.$route.meta.funcname, "canedit")) {
                    return false;
                }

                this.$store.dispatch('put', {url: "/reviewformsection/item/" + this.formData.id, payload: this.formData})
                .then((res) => {
                    if (res.data.error) {
                        vm.isErr = !vm.isErr;
                        if (Array.isArray(res.data.error)) {
                            vm.errMsg = res.data.error.map((e, index) => `${index + 1}. ${e.msg}`).join('\n');
                        } else {
                            vm.errMsg = res.data.error;
                        }
                    } else {
                        const index = this.detailItem.findIndex(item => item.id === this.formData.id);
                        if (index !== -1) {
                            const displayOptionType = vm.displayOptionList.find(type => type.value === this.formData.pmdisplayoptionid) || {};
                            
                            this.detailItem[index].changeWeightagetext = this.formData.changeWeightage === 1 ? 'Yes' : 'No';
                            this.detailItem[index].isCompulsorytext = this.formData.isCompulsory === 1 ? 'Yes' : 'No';
                            this.detailItem[index].displayoptiontext = displayOptionType.text || 'Unknown';
                            this.detailItem[index] = { ...this.formData };
                        }

                        this.itemdialog = false;
                        this.isEdit = false;
                        this.resetSelections();
                    }
                })
                .catch((err) => {
                    vm.isErr = !vm.isErr;
                    vm.errMsg = err;
                    console.log("err", err);
                });
            } else { //Add
                if (!this.CheckPermission(this.$route.meta.funcname, "canadd")) {
                    return false;
                }

                this.$store.dispatch('post', {url: "/reviewformsection/item/create", payload: this.formData})
                .then((res) => {
                    if (res.data.error) {
                        vm.isErr = !vm.isErr;
                        if (Array.isArray(res.data.error)) {
                            vm.errMsg = res.data.error.map((e, index) => `${index + 1}. ${e.msg}`).join('\n');
                        } else {
                            vm.errMsg = res.data.error;
                        }
                    } else {
                        const displayOptionType = vm.displayOptionList.find(type => type.value === res.data.result.pmdisplayoptionid) || {};

                        res.data.result.changeWeightagetext = res.data.result.changeWeightage === 1 ? 'Yes' : 'No';
                        res.data.result.isCompulsorytext = res.data.result.isCompulsory === 1 ? 'Yes' : 'No';
                        res.data.result.displayoptiontext = displayOptionType.text || 'Unknown';

                        this.detailItem.push(res.data.result);
                        this.itemdialog = false;
                        this.resetSelections();
                    }
                })
                .catch((err) => {
                    vm.isErr = !vm.isErr;
                    vm.errMsg = err;
                    console.log("err", err);
                });
            }
        },

        closeItemDialog() {
            this.itemdialog = false;
            this.isErr = false;
            // reset the form fields
            this.resetSelections();
        },

        resetSelections() {
            this.isEdit = false;
            this.formData = {
                pmreviewformsectionid: this.parentObject.sectid,
                secttype: this.parentObject.secttype,
                label: null,
                orderno: null,
                description: null,
                weightage: this.parentObject.sectweightage,
                changeWeightage: false,
                pmdisplayoptionid: null,
                isCompulsory: false,
                rectimestamp: null,
                updatedby: null,
                stamp: 0,
                userid: this.$store.getters.userId,
            }
        },          
    },

    mounted() {
        Promise.all([this.loadDisplayOption()])
        .then(() => {
            // After loading dependencies, load review form section
            return this.loadReviewFormSectionItem();
        })
        .then(() => {
            // Once everything is loaded, set loading to false
            this.loading = false;
        })
        .catch((err) => {
            // Handle any errors that occur during the process
            this.isErr = true;
            this.errMsg = err;
            console.error("Error:", err);
        });
    }
}
</script>

<style>

.v-input .v-input__control .v-input__slot .v-text-field__slot input::-webkit-outer-spin-button,
.v-input .v-input__control .v-input__slot .v-text-field__slot input::-webkit-inner-spin-button
{
-webkit-appearance: none;
margin: 0;
}
</style>
